import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { cn } from '@helpers/cn';
import type { TAsrExecution } from 'app/API/custom/asr-execution/types';
import { ReactNode, useEffect, useRef, useState } from 'react';
import type { AsrTranscript } from '@pages/Admin/AsrProxyConsolePage/api/types';
import { formatAudioSource } from '@pages/Admin/AsrProxyConsolePage/helpers/formatAudioSource';
import { formatDurationMs } from '@pages/Admin/AsrProxyConsolePage/helpers/formatDurationMs';
import { formatRetryPolicy } from '@pages/Admin/AsrProxyConsolePage/helpers/formatRetryPolicy';
import { getExecutionStageDurationMs } from '@pages/Admin/AsrProxyConsolePage/helpers/getExecutionStageDurationMs';
import { getModelSelectionSourceLabel } from '@pages/Admin/AsrProxyConsolePage/helpers/getModelSelectionSourceLabel';
import { PROVIDER_RESPONSE_PHASE } from '@pages/Admin/AsrProxyConsolePage/constants';
import { getTranscriptionRouteFinalPhase } from '@pages/Admin/AsrProxyConsolePage/helpers/getTranscriptionRouteFinalPhase';
import type { TTranscriptionRouteOutcome } from '@pages/Admin/AsrProxyConsolePage/types/TTranscriptionRouteOutcome';
import { QualityCheckGauges } from './components/QualityCheckGauges';
import { STEP_TITLES } from './constants';
import { RouteStepDetails } from './components/RouteStepDetails';
import type { TProps } from './types/TProps';

const PHASE_DURATION_MS = 1100;
const RESULT_REVEAL_DELAY_MS = 900;

type TFlowCheckProps = {
  number: number;
  title: string;
  detail: string;
  isActive: boolean;
  isComplete: boolean;
  isFailed?: boolean;
  isSelected?: boolean;
  onInspect: () => void;
  children?: ReactNode;
};

const FlowCheck = ({
  number,
  title,
  detail,
  isActive,
  isComplete,
  isFailed,
  isSelected,
  onInspect,
  children,
}: TFlowCheckProps) => (
  <section
    aria-current={isActive ? 'step' : undefined}
    className={cn(
      'relative w-[320px] shrink-0 animate-asrRouteReveal rounded-lg border bg-surface p-4 shadow-[0_8px_20px_var(--color-shadow)] transition-colors duration-500 motion-reduce:animate-none motion-reduce:transition-none',
      isActive && 'border-accent bg-accent-subtle shadow-[0_0_0_3px_var(--color-accent-subtle)]',
      isComplete && 'border-success',
      isFailed && 'border-danger',
      !isActive && !isComplete && !isFailed && 'border-border',
      isSelected && 'ring-2 ring-accent ring-offset-2 ring-offset-page',
    )}
  >
    {isActive ? (
      <span aria-hidden="true" className="absolute inset-x-0 top-0 h-0.5 rounded-t-lg animate-pulse bg-accent" />
    ) : null}
    <div className="flex items-start gap-3">
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-page text-xs font-semibold text-foreground-muted',
          isActive && 'border-accent text-accent',
          isComplete && 'border-success text-success',
          isFailed && 'border-danger text-danger',
        )}
      >
        {isActive ? (
          <span
            aria-hidden="true"
            className="size-3.5 animate-spin rounded-full border-2 border-accent border-r-transparent motion-reduce:animate-none"
          />
        ) : isFailed ? (
          '×'
        ) : isComplete ? (
          '✓'
        ) : (
          number
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 break-words text-sm font-semibold leading-5 text-foreground">{title}</h3>
          <button
            type="button"
            aria-label={`Details: ${title}`}
            className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-accent hover:bg-accent-subtle"
            onClick={onInspect}
          >
            Details
          </button>
        </div>
        <p className="mt-1 break-words text-xs leading-5 text-foreground-muted">{detail}</p>
        {children}
      </div>
    </div>
  </section>
);

const ExecutedArrow = () => (
  <svg
    aria-hidden="true"
    width="48"
    height="16"
    viewBox="0 0 48 16"
    className="mt-6 shrink-0 animate-asrRouteReveal text-accent motion-reduce:animate-none"
  >
    <line x1="0" y1="8" x2="39" y2="8" stroke="currentColor" strokeWidth="2" />
    <polygon points="38,2 48,8 38,14" fill="currentColor" />
    <circle cx="10" cy="8" r="2" fill="currentColor" className="animate-pulse motion-reduce:animate-none" />
  </svg>
);

const FactList = ({ facts }: { facts: { label: string; value: string | undefined }[] }) => (
  <dl className="mt-3 grid gap-1.5 text-[11px]">
    {facts
      .filter((fact): fact is { label: string; value: string } => fact.value !== undefined)
      .map((fact) => (
        <div key={fact.label} className="min-w-0 rounded-md border border-border bg-page px-3 py-2">
          <dt className="text-foreground-muted">{fact.label}</dt>
          <dd className="mt-0.5 break-words font-medium text-foreground [overflow-wrap:anywhere]">{fact.value}</dd>
        </div>
      ))}
  </dl>
);

const DecisionDestinations = ({ routes }: { routes: { condition: string; destination: string }[] }) => (
  <div className="mt-3 grid gap-1.5">
    {routes.map((route) => (
      <div
        key={route.condition}
        className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border border-border bg-page px-3 py-2 text-[11px]"
      >
        <span className="text-foreground-muted">{route.condition}</span>
        <span className="font-medium text-accent">→ {route.destination}</span>
      </div>
    ))}
  </div>
);

const NameChips = ({ names, emptyText }: { names: string[]; emptyText: string }) =>
  names.length === 0 ? (
    <p className="mt-2 text-[11px] text-foreground-muted">{emptyText}</p>
  ) : (
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {names.map((name) => (
        <li
          key={name}
          className="max-w-full break-words rounded-full bg-accent-subtle px-2 py-0.5 text-[11px] font-medium text-accent [overflow-wrap:anywhere]"
        >
          {name}
        </li>
      ))}
    </ul>
  );

const RecoveryConnector = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 py-2 text-[11px] text-foreground-muted">
    <span aria-hidden="true" className="h-6 shrink-0 border-l-2 border-dashed border-accent" />
    <span className="break-words">{label}</span>
  </div>
);

const RecoveryRoute = ({ execution, outcome }: { execution: TAsrExecution; outcome: TTranscriptionRouteOutcome }) => {
  const lastAttempt = execution.attempts.at(-1);
  const retriesUsed = lastAttempt?.transientErrors ?? 0;
  const isFailureRoute = outcome === 'response-failed';
  const isActive = isFailureRoute || retriesUsed > 0 || execution.fallbacksApplied > 0;
  return (
    <aside
      className={cn(
        'mt-5 w-[320px] animate-asrRouteReveal rounded-lg border border-dashed border-accent bg-accent-subtle p-4 transition-opacity duration-500 motion-reduce:animate-none motion-reduce:transition-none',
        !isActive && 'opacity-45',
      )}
      aria-label="Retry and fallback route"
    >
      <div className="text-xs font-semibold text-foreground">Recovery policy recorded for this job</div>
      <RecoveryConnector label="Transient provider or network error while waiting for the result" />
      <div
        className={cn(
          'rounded-md border border-dashed border-accent bg-surface p-3 transition-colors duration-500',
          retriesUsed > 0 && 'border-solid shadow-[0_0_0_3px_var(--color-accent-subtle)]',
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs font-semibold text-foreground">
          <span>Retry the same model</span>
          {outcome !== 'awaiting-response' ? <span className="text-accent">{retriesUsed} used</span> : null}
        </div>
        <p className="mt-1 break-words text-[11px] leading-4 text-foreground-muted">
          {formatRetryPolicy(execution.plan.retryPolicy)}
        </p>
      </div>
      <RecoveryConnector label="After the retries are exhausted" />
      <div
        className={cn(
          'rounded-md border border-dashed border-accent bg-surface p-3 transition-colors duration-500',
          execution.fallbacksApplied > 0 && 'border-solid shadow-[0_0_0_3px_var(--color-accent-subtle)]',
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs font-semibold text-foreground">
          <span>Select the next fallback model</span>
          {outcome !== 'awaiting-response' ? (
            <span className="text-accent">{execution.fallbacksApplied} applied</span>
          ) : null}
        </div>
        <p className="mt-1 break-words text-[11px] leading-4 text-foreground-muted">
          {execution.plan.fallbackModels.length > 0
            ? execution.plan.fallbackModels.join(', ')
            : 'No fallback models configured'}
        </p>
      </div>
      <RecoveryConnector label="No eligible fallback, or every fallback failed" />
      <div
        className={cn(
          'rounded-md border border-dashed border-danger bg-surface p-3 text-xs font-semibold text-danger transition-colors duration-500',
          isFailureRoute && 'border-solid shadow-[0_0_0_3px_var(--color-accent-subtle)]',
        )}
      >
        Return the transcription error
      </div>
    </aside>
  );
};

const describeTranscript = (transcript: AsrTranscript) => [
  { label: 'Utterances', value: String(transcript.utterances.length) },
  { label: 'Audio duration', value: transcript.audio_duration != null ? `${transcript.audio_duration} s` : undefined },
  { label: 'Language', value: transcript.language_code },
  { label: 'Confidence', value: transcript.confidence != null ? transcript.confidence.toFixed(2) : undefined },
];

export const TranscriptionRouteAnimation = ({
  runId,
  jobId,
  mode,
  execution,
  audioUrl,
  outcome,
  transcript,
  errorMessage,
  onAnimationFinished,
  onClose,
}: TProps) => {
  const finalPhase = getTranscriptionRouteFinalPhase(outcome, execution.stages, execution.attempts);
  const [phase, setPhase] = useState(mode === 'review' ? finalPhase : 0);
  const [isPlaying, setIsPlaying] = useState(mode === 'live');
  const [isPaused, setIsPaused] = useState(false);
  const [isAutoFollowing, setIsAutoFollowing] = useState(true);
  const [selectedPhase, setSelectedPhase] = useState<number | null>(null);
  const routeRef = useRef<HTMLDivElement>(null);
  const lastRunIdRef = useRef(runId);
  const hasSucceeded = outcome === 'response-received';
  const hasFailed = outcome === 'response-failed';
  const { plan, stages } = execution;
  const lastAttempt = execution.attempts.at(-1);

  useEffect(() => {
    if (lastRunIdRef.current === runId) {
      return;
    }
    lastRunIdRef.current = runId;
    setPhase(mode === 'review' ? finalPhase : 0);
    setIsPlaying(mode === 'live');
    setSelectedPhase(null);
    setIsPaused(false);
    setIsAutoFollowing(true);
  }, [finalPhase, mode, runId]);

  useEffect(() => {
    if (!isPlaying || isPaused) {
      return;
    }
    const interval = window.setInterval(() => {
      setPhase((currentPhase) => {
        if (currentPhase === PROVIDER_RESPONSE_PHASE && outcome === 'awaiting-response') {
          return currentPhase;
        }
        return Math.min(currentPhase + 1, finalPhase);
      });
    }, PHASE_DURATION_MS);
    return () => window.clearInterval(interval);
  }, [finalPhase, isPaused, isPlaying, outcome]);

  useEffect(() => {
    if (isPaused || !isPlaying || !isAutoFollowing) {
      return;
    }
    const route = routeRef.current;
    const activeStep = route?.querySelector<HTMLElement>('[aria-current="step"]');
    if (!route || !activeStep) {
      return;
    }
    const centeredStepOffset = activeStep.offsetLeft - (route.clientWidth - activeStep.offsetWidth) / 2;
    route.scrollTo?.({ behavior: 'smooth', left: Math.max(0, centeredStepOffset) });
  }, [isAutoFollowing, isPaused, isPlaying, phase]);

  useEffect(() => {
    if (mode !== 'live' || isPaused || !isPlaying || phase !== finalPhase || outcome !== 'response-received') {
      return;
    }
    const timeout = window.setTimeout(onAnimationFinished, RESULT_REVEAL_DELAY_MS);
    return () => window.clearTimeout(timeout);
  }, [finalPhase, isPaused, isPlaying, mode, onAnimationFinished, outcome, phase]);

  const replay = () => {
    setPhase(0);
    setSelectedPhase(null);
    setIsPaused(false);
    setIsAutoFollowing(true);
    setIsPlaying(true);
  };
  const inspectStep = (stepPhase: number) => {
    setIsPaused(true);
    setSelectedPhase(stepPhase);
  };
  const stepInspection = (stepPhase: number) => ({
    isSelected: selectedPhase === stepPhase,
    onInspect: () => inspectStep(stepPhase),
  });
  const isFinished = phase === finalPhase && outcome !== 'awaiting-response';
  const showPauseControl = isPlaying && !isFinished;
  const failedPhase = hasFailed ? finalPhase : null;
  const isStepFailed = (stepPhase: number) => failedPhase === stepPhase && phase >= stepPhase;
  const isStepComplete = (stepPhase: number) => (phase > stepPhase || isFinished) && failedPhase !== stepPhase;
  const isStepActive = (stepPhase: number) => phase === stepPhase && !isFinished;
  const showsResultSteps = phase >= 6 && (hasSucceeded ? transcript !== null : hasFailed);
  const failureAlert = (stepPhase: number) =>
    isStepFailed(stepPhase) && errorMessage ? (
      <p
        role="alert"
        className="mt-3 whitespace-pre-wrap break-words rounded-md border border-danger bg-page px-3 py-2 text-[11px] leading-4 text-danger"
      >
        {errorMessage}
      </p>
    ) : null;

  const appliedNormalizers = execution.normalizersApplied ?? transcript?.provenance.normalizers ?? [];
  const firstUtterance = transcript?.utterances[0];
  const providerDuration = formatDurationMs(
    getExecutionStageDurationMs(stages, 'provider_submitted', 'provider_result_received'),
  );
  const totalDuration = formatDurationMs(
    getExecutionStageDurationMs(stages, 'request_received', hasFailed ? 'failed' : 'completed'),
  );

  return (
    <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col" aria-label="ASR Proxy algorithm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-medium text-foreground">ASR Proxy algorithm</h2>
          <p className="mt-1 text-sm text-foreground-muted">
            Built from the execution plan the proxy recorded when it accepted job {jobId} and from the facts it stored
            while running it. Step pacing is illustrative; durations come from the recorded stages.
          </p>
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
          <span className="whitespace-nowrap rounded-full border border-accent bg-accent-subtle px-3 py-1 text-xs font-medium text-accent">
            {plan.provider} · {plan.providerModel}
          </span>
          <OutlinedButton
            className="!w-[104px] whitespace-nowrap"
            disabled={!showPauseControl}
            onClick={() => setIsPaused((paused) => !paused)}
          >
            {isPaused ? 'Resume' : 'Pause'}
          </OutlinedButton>
          <OutlinedButton className="!w-[104px] whitespace-nowrap" onClick={replay}>
            Replay
          </OutlinedButton>
          <OutlinedButton className="!w-[168px] whitespace-nowrap" onClick={onClose}>
            {mode === 'live' && !isFinished ? 'Skip animation' : 'Back to transcript'}
          </OutlinedButton>
        </div>
      </div>

      <div className="mb-3 h-1 overflow-hidden rounded-full bg-surface-muted">
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-500 motion-reduce:transition-none"
          style={{ width: `${((phase + 1) / (finalPhase + 1)) * 100}%` }}
        />
      </div>
      <p role="status" className="mb-3 min-h-4 text-xs text-foreground-muted">
        {isPaused
          ? "Paused. Open a step's details to inspect its intermediate result; Resume continues from the current step."
          : isFinished && hasFailed
            ? `Failed at step ${finalPhase + 1} — ${STEP_TITLES[finalPhase]}. Open its details for the recorded error.`
            : isFinished && mode === 'review'
              ? 'Completed route. Replay animates it again from the first step.'
              : ''}
      </p>

      <div
        ref={routeRef}
        aria-label="ASR route steps"
        className="min-h-0 min-w-0 w-full max-w-full flex-1 overflow-x-auto overflow-y-auto px-1 pb-8"
        onPointerDown={() => setIsAutoFollowing(false)}
        onTouchStart={() => setIsAutoFollowing(false)}
        onWheel={() => setIsAutoFollowing(false)}
      >
        <div className="mx-auto flex min-w-max items-start py-6">
          <FlowCheck
            number={1}
            title="Request accepted"
            detail="Audio, selected model and request options enter ASR Proxy."
            isActive={isStepActive(0)}
            isComplete={isStepComplete(0)}
            {...stepInspection(0)}
          >
            <FactList
              facts={[
                { label: 'Audio', value: audioUrl ? formatAudioSource(audioUrl) : undefined },
                { label: 'Model', value: plan.model },
                { label: 'Model selected by', value: getModelSelectionSourceLabel(plan.modelSelectionSource) },
                { label: 'Mode', value: plan.synchronous ? 'synchronous' : 'queued workflow' },
              ]}
            />
          </FlowCheck>

          {phase >= 1 ? <ExecutedArrow /> : null}
          {phase >= 1 ? (
            <FlowCheck
              number={2}
              title="Request persisted"
              detail={
                plan.synchronous
                  ? 'The proxy stores the request as a job and runs it in the same call.'
                  : 'The proxy stores the request as a job and starts the durable workflow.'
              }
              isActive={isStepActive(1)}
              isComplete={isStepComplete(1)}
              isFailed={isStepFailed(1)}
              {...stepInspection(1)}
            >
              {failureAlert(1)}
            </FlowCheck>
          ) : null}

          {phase >= 2 ? <ExecutedArrow /> : null}
          {phase >= 2 ? (
            <FlowCheck
              number={3}
              title="Provider, model and settings resolved"
              detail="The registered provider is matched to the model and the pipeline configured for this request is read."
              isActive={isStepActive(2)}
              isComplete={isStepComplete(2)}
              {...stepInspection(2)}
            >
              <FactList
                facts={[
                  { label: 'Provider', value: plan.provider },
                  { label: 'Provider model', value: plan.providerModel },
                ]}
              />
              <p className="mt-3 text-[11px] font-semibold text-foreground">Normalizers</p>
              <NameChips names={plan.normalizers} emptyText="No normalizers configured" />
              <p className="mt-3 text-[11px] font-semibold text-foreground">Quality checkers</p>
              <NameChips names={plan.qualityCheckers} emptyText="No quality checkers configured" />
            </FlowCheck>
          ) : null}

          {phase >= 3 ? <ExecutedArrow /> : null}
          {phase >= 3 ? (
            <FlowCheck
              number={4}
              title="Parameters checked and prepared"
              detail="Saved defaults are merged with the request overrides, validated and turned into provider parameters."
              isActive={isStepActive(3)}
              isComplete={isStepComplete(3)}
              {...stepInspection(3)}
            >
              <p className="mt-3 text-[11px] font-semibold text-foreground">Usable options</p>
              <NameChips names={Object.keys(plan.options)} emptyText="No provider options" />
              {plan.ignoredOptions.length > 0 ? (
                <>
                  <p className="mt-3 text-[11px] font-semibold text-foreground">Ignored options</p>
                  <NameChips names={plan.ignoredOptions} emptyText="" />
                </>
              ) : null}
              <DecisionDestinations
                routes={[
                  { condition: 'Parameters valid', destination: 'Send to provider' },
                  { condition: 'Parameters invalid', destination: 'Return request error' },
                ]}
              />
            </FlowCheck>
          ) : null}

          {phase >= 4 ? <ExecutedArrow /> : null}
          {phase >= 4 ? (
            <FlowCheck
              number={5}
              title={`Request sent to ${plan.provider}`}
              detail="The provider transport receives the prepared transcription request."
              isActive={isStepActive(4)}
              isComplete={isStepComplete(4)}
              isFailed={isStepFailed(4)}
              {...stepInspection(4)}
            >
              {failureAlert(4)}
              <FactList facts={[{ label: 'Provider transcript', value: lastAttempt?.providerTranscriptId }]} />
            </FlowCheck>
          ) : null}

          {phase >= PROVIDER_RESPONSE_PHASE ? <ExecutedArrow /> : null}
          {phase >= PROVIDER_RESPONSE_PHASE ? (
            <div className="w-[320px] shrink-0">
              <FlowCheck
                number={6}
                title="Provider response checked"
                detail={
                  isStepFailed(PROVIDER_RESPONSE_PHASE)
                    ? 'The provider result cannot be accepted, so the job ends with an error.'
                    : outcome === 'awaiting-response'
                      ? `Waiting for ${plan.provider} to return a result.`
                      : `${plan.provider} returned a usable transcript, so the main route continues.`
                }
                isActive={isStepActive(PROVIDER_RESPONSE_PHASE) && !hasFailed}
                isComplete={isStepComplete(PROVIDER_RESPONSE_PHASE) && outcome !== 'awaiting-response'}
                isFailed={isStepFailed(PROVIDER_RESPONSE_PHASE)}
                {...stepInspection(5)}
              >
                {failureAlert(PROVIDER_RESPONSE_PHASE)}
                <FactList facts={[{ label: 'Provider time', value: providerDuration }]} />
                <DecisionDestinations
                  routes={[
                    { condition: 'Usable transcript', destination: 'AssemblyAI-first conversion' },
                    { condition: 'Transient error', destination: 'Retry policy below' },
                    { condition: 'Unrecoverable error', destination: 'Fallback selection' },
                  ]}
                />
              </FlowCheck>
              <RecoveryRoute execution={execution} outcome={outcome} />
            </div>
          ) : null}

          {showsResultSteps ? <ExecutedArrow /> : null}
          {showsResultSteps ? (
            <FlowCheck
              number={7}
              title="Converted to AssemblyAI-first"
              detail="The provider implementation converts its native response to the shared transcript contract."
              isActive={isStepActive(6)}
              isComplete={isStepComplete(6)}
              isFailed={isStepFailed(6)}
              {...stepInspection(6)}
            >
              {failureAlert(6)}
              {transcript ? (
                <FactList facts={describeTranscript(transcript)} />
              ) : execution.mappedTranscriptPreview ? (
                <FactList
                  facts={[
                    { label: 'Utterances', value: String(execution.mappedTranscriptPreview.utteranceCount) },
                    { label: 'Characters', value: String(execution.mappedTranscriptPreview.characterCount) },
                  ]}
                />
              ) : null}
            </FlowCheck>
          ) : null}

          {showsResultSteps && phase >= 7 ? <ExecutedArrow /> : null}
          {showsResultSteps && phase >= 7 ? (
            <FlowCheck
              number={8}
              title="Normalizers applied in order"
              detail="The normalizers that actually ran for this transcript, in the order defined by the model."
              isActive={isStepActive(7)}
              isComplete={isStepComplete(7)}
              isFailed={isStepFailed(7)}
              {...stepInspection(7)}
            >
              {failureAlert(7)}
              <NameChips
                names={appliedNormalizers}
                emptyText={isStepFailed(7) ? 'Normalization did not finish.' : 'No normalizers ran for this request.'}
              />
              {firstUtterance ? (
                <div className="mt-3 rounded-md border border-border bg-page p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
                      First utterance
                    </span>
                    <span className="rounded-full bg-accent-subtle px-2 py-0.5 text-[11px] font-medium text-accent">
                      {firstUtterance.speaker}
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-4 break-words text-xs leading-5 text-foreground">
                    “{firstUtterance.text}”
                  </p>
                </div>
              ) : null}
            </FlowCheck>
          ) : null}

          {showsResultSteps && phase >= 8 ? <ExecutedArrow /> : null}
          {showsResultSteps && phase >= 8 ? (
            <FlowCheck
              number={9}
              title="Quality checks evaluated"
              detail="Each enabled checker scored the final normalized transcript from 0 to 100."
              isActive={isStepActive(8)}
              isComplete={isStepComplete(8)}
              isFailed={isStepFailed(8)}
              {...stepInspection(8)}
            >
              {failureAlert(8)}
              {isStepFailed(8) ? null : <QualityCheckGauges checks={transcript?.quality_checks ?? []} />}
            </FlowCheck>
          ) : null}

          {showsResultSteps && phase >= 9 ? <ExecutedArrow /> : null}
          {showsResultSteps && phase >= 9 ? (
            <FlowCheck
              number={10}
              title="Normalized transcript returned"
              detail="The accepted AssemblyAI-first result is stored and returned to the caller."
              isActive={isStepActive(9)}
              isComplete={isStepComplete(9)}
              isFailed={isStepFailed(9)}
              {...stepInspection(9)}
            >
              {failureAlert(9)}
              <FactList
                facts={[
                  { label: 'Job', value: jobId },
                  { label: 'Attempts', value: String(execution.attempts.length) },
                  { label: 'Retries used', value: String(lastAttempt?.transientErrors ?? 0) },
                  { label: 'Fallbacks applied', value: String(execution.fallbacksApplied) },
                  { label: 'Total time', value: totalDuration },
                ]}
              />
            </FlowCheck>
          ) : null}
        </div>
      </div>

      {selectedPhase !== null ? (
        <RouteStepDetails
          phase={selectedPhase}
          failedPhase={failedPhase}
          jobId={jobId}
          execution={execution}
          audioUrl={audioUrl}
          outcome={outcome}
          transcript={transcript}
          errorMessage={errorMessage}
          onClose={() => setSelectedPhase(null)}
        />
      ) : null}
    </div>
  );
};
